import { SxProps, Theme } from "@mui/material"
import Table from "@mui/material/Table"
import TableBody from "@mui/material/TableBody"
import TableCell from "@mui/material/TableCell"
import TableContainer from "@mui/material/TableContainer"
import TableHead from "@mui/material/TableHead"
import TableRow from "@mui/material/TableRow"
import React, { memo, useCallback } from "react"

export interface MuiTableColumn<T> {
    id: keyof T | "index" | "custom"
    label: string | React.ReactNode | ""
    width?: number
    align?: "right" | "center" | "left"
    tableCellStyle?: string
    format?: (value: T[keyof T], row: T, rowIndex: number) => string
    render?: (value: T[keyof T], row: T, rowIndex: number) => React.ReactNode
}

type Props<T> = {
    columns: MuiTableColumn<T>[]
    rows: T[]
    tableClassName?: string
    containerSx?: SxProps<Theme>
    stickyHeader?: boolean
    showInsideBorders?: boolean
}

const tableHeaderCellStyles: SxProps<Theme> = {
    color: "#70758D",
    fontSize: "11px",
    padding: "8px",
    borderBottom: "none",
}

const tableCellStyles: SxProps<Theme> = {
    color: "#101941",
    fontSize: "12px",
    padding: "8px",
}

type RowProps<T> = {
    row: T
    columns: MuiTableColumn<T>[]
    rowIndex: number
    isLast: boolean
    showInsideBorders: boolean
    insideBorder: string | undefined
    renderCell: (row: T, column: MuiTableColumn<T>, rowIndex: number) => React.ReactNode
}

function TableRowInner<T extends object>({
    row,
    columns,
    rowIndex,
    isLast,
    showInsideBorders,
    insideBorder,
    renderCell,
}: RowProps<T>) {
    return (
        <TableRow key={`row-${rowIndex}`}>
            {columns.map((column, colIndex) => (
                <TableCell
                    key={`cell-${rowIndex}-${colIndex}`}
                    align={column.align}
                    className={column.tableCellStyle}
                    sx={{
                        ...tableCellStyles,
                        borderBottom: isLast ? "none" : showInsideBorders ? insideBorder : undefined,
                        borderRight: colIndex < columns.length - 1 ? insideBorder : undefined,
                        width: column.width,
                    }}>
                    {renderCell(row, column, rowIndex)}
                </TableCell>
            ))}
        </TableRow>
    )
}

const MemoizedTableRow = memo(TableRowInner) as typeof TableRowInner

const MuiTable = <T extends object>(props: Props<T>) => {
    const { columns, rows, containerSx = {}, tableClassName, showInsideBorders = false } = props
    const insideBorder = showInsideBorders ? "1px solid #E5E7EB" : undefined

    const renderCell = useCallback(
        (row: T, column: MuiTableColumn<T>, rowIndex: number) => {
            if (Object.keys(column).length === 0) return
            if (column.id === "index") return rowIndex + 1
            if (column.id === "custom") {
                if (column.render) return column.render(undefined as any, row, rowIndex)
                return "-"
            }
            if (!row[column.id] && !column.render) return "-"

            const value = row[column.id]

            if (column.render) return column.render(value, row, rowIndex)
            if (column.format) return column.format(value, row, rowIndex)

            return String(value)
        },
        [columns]
    )

    return (
        <TableContainer sx={containerSx}>
            <Table stickyHeader={props.stickyHeader} className={tableClassName}>
                <TableHead>
                    <TableRow>
                        {columns.map((column, index) => (
                            <TableCell
                                size="small"
                                key={column.id ? `${String(column.id)}-${index}` : index}
                                align={column.align}
                                sx={{
                                    ...tableHeaderCellStyles,
                                    width: column.width,
                                    borderRight: index < columns.length - 1 ? insideBorder : undefined,
                                    borderBottom: showInsideBorders ? insideBorder : undefined,
                                }}>
                                {column.label}
                            </TableCell>
                        ))}
                    </TableRow>
                </TableHead>
                <TableBody>
                    {rows && rows.length > 0 ? (
                        rows.map((row, rowIndex) => (
                            <MemoizedTableRow
                                key={`row-${rowIndex}`}
                                row={row}
                                columns={columns}
                                rowIndex={rowIndex}
                                isLast={rowIndex === rows.length - 1}
                                showInsideBorders={showInsideBorders}
                                insideBorder={insideBorder}
                                renderCell={renderCell}
                            />
                        ))
                    ) : (
                        <TableRow>
                            <TableCell
                                colSpan={columns.length}
                                align="center"
                                className="p-16"
                                sx={{
                                    ...tableCellStyles,
                                    borderBottom: "none",
                                }}>
                                No Data Available
                            </TableCell>
                        </TableRow>
                    )}
                </TableBody>
            </Table>
        </TableContainer>
    )
}

export default MuiTable
